import {
  type FunctionComponent,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button, Modal, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { StarOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { useBoolean } from 'ahooks';
import type { IRecipeShape } from '../utils/IRecipeShape';
import { OverlayButton } from './styles';
import { roles } from '../../../lib/userRoles';
import { AuthGuard } from '../../AuthGuard';
import {
  GET_RECIPE_COLLECTIONS,
  type IResponse as QueryResponse,
  type IVariables as QueryVariables,
} from '../../../gql/get-recipe-collections';
import { handleError } from '../../../lib/handleError';
import { CollectionsList } from './CollectionsList';
import { useRapsPagination } from '../../../pages/Search/hooks/usePagination';
import { PaginatedResultList } from '../../../pages/Search/components/PaginatedResultList';

const PUBLIC_TAB_KEY = 'public';
const PRIVATE_TAB_KEY = 'private';

type IProps = {
  children?: (
    onClick: () => void,
    recipeHasCollections?: boolean,
  ) => ReactElement;
  recipe: IRecipeShape;
};

const modalPageSize = 20;

export const AddToCollectionAction: FunctionComponent<IProps> = ({
  children,
  recipe,
}) => {
  const client = useApolloClient();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [activeTab, setActionTab] = useState(PRIVATE_TAB_KEY);
  const [privateCollections, setPrivateCollections] = useState<QueryResponse>();
  const [publicCollections, setPublicCollections] = useState<QueryResponse>();

  const [
    showModal,
    { setTrue: setShowModalTrue, setFalse: setShowModalFalse },
  ] = useBoolean();

  const [hasCollection, setHasCollection] = useState(false);

  const { cursor, itemsPerPage, onChange } = useRapsPagination({
    pageSize: modalPageSize,
    pageParam: 'modalPage',
    pageSizeParam: 'modalPageSize',
  });

  const getCollections = useCallback(
    async (ownedByUser: boolean) => {
      setLoading(true);

      const { data, error } = await client.query<QueryResponse, QueryVariables>(
        {
          fetchPolicy: 'network-only',
          query: GET_RECIPE_COLLECTIONS,
          variables: {
            ownedByUser,
            recipeId: recipe._id,
            cursor,
            itemsPerPage,
          },
        },
      );

      if (error) {
        handleError(error);
      }

      setLoading(false);

      if (ownedByUser) {
        setPrivateCollections(data);
      } else {
        setPublicCollections(data);
      }

      setHasCollection(!!recipe.collections?.totalCount);
    },
    [client, recipe.collections?.totalCount, recipe._id, cursor, itemsPerPage],
  );

  useEffect(() => {
    getCollections(activeTab === PRIVATE_TAB_KEY);
  }, [getCollections, itemsPerPage, cursor, activeTab]);

  const openModal = useCallback(async () => {
    onChange(1, modalPageSize);
    setShowModalTrue();
    await getCollections(true);
  }, [getCollections, setShowModalTrue, onChange]);

  const handleTabsChange = useCallback(
    async (tab: string) => {
      onChange(0, modalPageSize);
      setActionTab(tab);
      await getCollections(tab === PRIVATE_TAB_KEY);
    },
    [getCollections, onChange],
  );

  const renderACollectionlist = useCallback(
    (ownedByUser: boolean) => {
      const totalCount = ownedByUser
        ? privateCollections?.collections.totalCount
        : publicCollections?.collections.totalCount;
      const itemCount = ownedByUser
        ? privateCollections?.collections.items.length
        : publicCollections?.collections.items.length;

      return (
        <PaginatedResultList
          totalCount={totalCount ?? 0}
          itemLength={itemCount ?? 0}
          searchParam="modalPage"
          pageSizeSearchParam="modalPageSize"
          defaultPageSize={modalPageSize}
        >
          <CollectionsList
            recipe={recipe}
            data={ownedByUser ? privateCollections : publicCollections}
            onAfterAddRecipe={() => getCollections(ownedByUser)}
            isPublic={!ownedByUser}
            loading={loading}
            onActionTrigger={() => {
              // displaying loading spinner when an action is triggered
              setLoading(true);
            }}
          />
        </PaginatedResultList>
      );
    },
    [getCollections, loading, privateCollections, publicCollections, recipe],
  );

  const items = useMemo(
    () => [
      {
        label: t('collections.public'),
        key: PUBLIC_TAB_KEY,
        children: renderACollectionlist(false),
      },
      {
        label: t('collections.private'),
        key: PRIVATE_TAB_KEY,
        children: renderACollectionlist(true),
      },
    ],
    [renderACollectionlist, t],
  );

  return (
    <>
      <Modal
        open={showModal}
        onCancel={setShowModalFalse}
        title={t('collections.addToRecipeCollection')}
        footer={
          <Button type="primary" onClick={setShowModalFalse}>
            {t('collections.closeAndSaveModal')}
          </Button>
        }
      >
        <AuthGuard roles={[roles.collectionEditor]}>
          {(isGranted) =>
            isGranted && !!recipe.number ? (
              <Tabs
                items={items}
                activeKey={activeTab}
                onChange={handleTabsChange}
                animated
              />
            ) : (
              renderACollectionlist(true)
            )
          }
        </AuthGuard>
      </Modal>
      {children ? (
        children(openModal, hasCollection)
      ) : (
        <OverlayButton
          onClick={openModal}
          shape="circle"
          type="primary"
          ghost={!hasCollection}
          icon={<StarOutlined />}
        />
      )}
    </>
  );
};
